import { Badge, BlockStack, Box, Button, Card, Icon, IndexTable, InlineStack, Link, Text } from '@shopify/polaris';
// import { useGetTrip_manifest_invoices, TypedTrip_manifest_invoice } from 'queries/trip_manifest_invoice.query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import helpers from 'helpers/index';
import Pagination from 'components/pagination';
import dateandtime from 'date-and-time';
import { BillFilledIcon, CashDollarIcon, EditIcon, InfoIcon, RefreshIcon, TaxFilledIcon } from '@shopify/polaris-icons';
import __ from 'languages/index';
import { TThaiHDonLabel, useMutateGetCurrentInvoiceStatus, useGetInvoices, TypedInvoices } from 'queries/invoices.query';
import SimpleFilter from 'components/simpleFilter';

export default function InvoiceSigned({ currentInvoiceCallback }: { currentInvoiceCallback: (invoiceID) => void }) {
  const rootSearchPath = window.location.search;
  let StringQuery: any = helpers.ExtractUrl(rootSearchPath) || false;

  const initialQuery = useMemo(
    () => ({
      query: '',
      page: 1,
      limit: 200,
      sort: 'signedAt:desc',
      is_signed: 1,
    }),
    []
  );

  const [mainQuery, setMainQuery] = useState<any>({
    ...initialQuery,
    ...StringQuery,
  });

  const { data, isFetching: loading } = useGetInvoices(mainQuery);

  const [entities, setEntities] = useState<TypedInvoices[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [sum, setSum] = useState({ count: 0, TgThTien: 0, TgTThue: 0, TgTTTBSo: 0 });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 800px)');
    const handleChange = (event: MediaQueryListEvent | MediaQueryList) => {
      const matches = 'matches' in event ? event.matches : mediaQuery.matches;
      setIsMobile(matches);
    };

    handleChange(mediaQuery);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange as (event: MediaQueryListEvent) => void);
    } else {
      mediaQuery.addListener(handleChange as (event: MediaQueryListEvent) => void);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange as (event: MediaQueryListEvent) => void);
      } else {
        mediaQuery.removeListener(handleChange as (event: MediaQueryListEvent) => void);
      }
    };
  }, []);

  useEffect(() => {
    if (data) {
      let { body, totalItems } = data;
      let { records, sum } = body;
      setEntities(records);
      setTotalItems(totalItems);
      setSum(sum);
    }
  }, [data]);

  const onChangePagination = useCallback((numPage: number, limit: number) => {
    setMainQuery((prevMainQuery) => ({ ...prevMainQuery, page: numPage, limit: limit }));
  }, []);

  useEffect(() => {
    let buildURLSearch = helpers.buildEndUrl(mainQuery);
    if (window.location.search !== buildURLSearch) {
      window.history.replaceState(null, 'Dashboard', '/exported' + buildURLSearch);
    }
  }, [mainQuery]);

  const filterCallback = useCallback(
    (value: any) => {
      if (value === false) {
        return setMainQuery(initialQuery);
      }
      setMainQuery((oldValue) => ({
        ...oldValue,
        ...value,
      }));
    },
    [initialQuery]
  );

  /** lấy status của hóa đơn hiện tại */
  const { mutateAsync: fetchCurrentInvoiceStatus } = useMutateGetCurrentInvoiceStatus();
  const [currentInvoiceDetailReload, setCurrentInvoiceDetailReload] = useState(null);
  const fetchCurrentInvoiceStatusCallback = useCallback(
    async (invoiceID) => {
      setCurrentInvoiceDetailReload(invoiceID);
      try {
        await fetchCurrentInvoiceStatus(invoiceID);
      } catch (e) {}
      setCurrentInvoiceDetailReload(null);
    },
    [entities]
  );

  const resourceName = {
    singular: 'invoice',
    plural: 'invoices',
  };

  const rowMarkup = useMemo(
    () =>
      entities.map(({ trip_manifest_invoice, ...invoices }, index) => {
        const tenTThaiHDon = TThaiHDonLabel[invoices.maTThaiHDon] || '--';
        const invoiceId = String(trip_manifest_invoice.id ?? `invoice-${index}`);
        const taxType = invoices.TaxType || 'DEFAULT';
        const journey = trip_manifest_invoice.trip_manifest?.trip?.journey;
        const journeyName = journey?.journey_name || `${journey?.journey_from || ''} - ${journey?.journey_to || ''}`.trim() || '--';
        const createdAt = invoices.createdAt ? dateandtime.format(new Date(Number(invoices.createdAt)), 'HH:mm DD/MM/YYYY') : '--';
        const signedAt = invoices.signedAt ? dateandtime.format(new Date(Number(invoices.signedAt)), 'HH:mm DD/MM/YYYY') : '--';
        const signedBy = (invoices as any)?.user?.display_name || (invoices as any)?.author_data?.display_name || '--';
        const pdfUrl = process.env.REACT_APP_BACKEND_URL + `/invoices/view_pdf/${invoices.maTraCuu}`;
        const departureDate = trip_manifest_invoice.trip_manifest?.departure_date;
        const datedy = departureDate ? dateandtime.format(new Date(Number(departureDate)), 'YYYY-MM-DD') : '';
        const departureDateAfterFormat = departureDate ? dateandtime.format(new Date(Number(departureDate)), 'DD-MM-YYYY') : '';
        const tripTime = trip_manifest_invoice.trip_manifest?.departure_hour || '--';
        const journeyId = trip_manifest_invoice.trip_manifest?.trip?.journey?.id;
        const time_id = trip_manifest_invoice.trip_manifest?.time_id;
        const ticketManagersLink = `/ticket_managers?d=${datedy}&journey_id=${String(journeyId ?? '')}&htext=${tripTime}&h=${String(time_id ?? '')}`;

        return (
          <IndexTable.Row id={invoiceId} key={invoiceId} position={index}>
            <IndexTable.Cell>
              <Text variant="bodyMd" fontWeight="bold" as="span">
                <Link url={pdfUrl || '#'} target="_blank">
                  {invoices.shDon || '--'}
                </Link>
              </Text>
            </IndexTable.Cell>
            <IndexTable.Cell>{signedBy}</IndexTable.Cell>
            <IndexTable.Cell>{signedAt}</IndexTable.Cell>
            <IndexTable.Cell>
              {invoices.ghiChuCQT ? (
                <Text as="p" tone="critical">
                  {invoices.ghiChuCQT}
                </Text>
              ) : null}

              {invoices.mccqt ? (
                <Text as="p" fontWeight="bold">
                  {invoices.mccqt}
                </Text>
              ) : (
                <BlockStack>
                  <Text as="p" tone="critical">
                    {tenTThaiHDon}
                  </Text>
                  <InlineStack gap={'200'} wrap={false}>
                    <Button variant="monochromePlain" size="slim" icon={EditIcon} onClick={() => currentInvoiceCallback(invoices.invoice_id)}>
                      Edit
                    </Button>
                    <Button
                      icon={RefreshIcon}
                      // disabled={!!invoices.mccqt}
                      variant="monochromePlain"
                      size="slim"
                      loading={currentInvoiceDetailReload === invoices.invoice_id}
                      onClick={() => fetchCurrentInvoiceStatusCallback(invoices.invoice_id)}
                    >
                      Reload
                    </Button>
                  </InlineStack>
                </BlockStack>
              )}
            </IndexTable.Cell>
            <IndexTable.Cell>{trip_manifest_invoice?.trip_manifest?.vehicle?.license_plate || '--'}</IndexTable.Cell>
            <IndexTable.Cell>
              <Link removeUnderline url={ticketManagersLink}>
                <Text as="span" fontWeight="bold">
                  <Badge tone="success">{tripTime}</Badge> • {journeyName}
                </Text>
              </Link>
            </IndexTable.Cell>
            <IndexTable.Cell>{departureDateAfterFormat}</IndexTable.Cell>
            <IndexTable.Cell>
              <Badge size="small" tone={taxType === 'COMPANY' ? 'critical' : 'info'}>
                {__('tax_type_' + taxType)}
              </Badge>
            </IndexTable.Cell>

            <IndexTable.Cell>{invoices.NMua_HVTNMHang || '--'}</IndexTable.Cell>
            <IndexTable.Cell>{invoices.NMua_CCCDan || '--'}</IndexTable.Cell>
            <IndexTable.Cell>{invoices.NMua_Ten || '--'}</IndexTable.Cell>
            <IndexTable.Cell>{invoices.NMua_MST || '--'}</IndexTable.Cell>
            <IndexTable.Cell>{invoices.NMua_SDThoai || '--'}</IndexTable.Cell>
            <IndexTable.Cell>{invoices.NMua_DCTDTu || '--'}</IndexTable.Cell>
            <IndexTable.Cell>{invoices.NMua_DChi || '--'}</IndexTable.Cell>
            <IndexTable.Cell>
              <Text as="span" alignment="end" numeric>
                {helpers.formatNumberWithCommas(invoices.TgTThue)}
              </Text>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <Text as="span" alignment="end" numeric>
                {helpers.formatNumberWithCommas(invoices.TgThTien)}
              </Text>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <Text as="span" alignment="end" numeric>
                {helpers.formatNumberWithCommas(invoices.TgTTTBSo)}
              </Text>
            </IndexTable.Cell>
            <IndexTable.Cell>{createdAt}</IndexTable.Cell>
            <IndexTable.Cell>{invoices.maTraCuu || '--'}</IndexTable.Cell>
            <IndexTable.Cell>{invoices.MTChieu || '--'}</IndexTable.Cell>
          </IndexTable.Row>
        );
      }),
    [entities, currentInvoiceDetailReload]
  );

  return (
    <BlockStack gap="400">
      <BlockStack gap="200">
        <InlineStack gap={isMobile ? '200' : '400'} wrap>
          <Box minWidth={isMobile ? '100%' : '220px'} width={isMobile ? '100%' : undefined}>
            <Card padding="300">
              <BlockStack gap="100">
                <InlineStack gap="200" blockAlign="center" align="start" wrap={false}>
                  <span>
                    <Icon source={InfoIcon} tone="subdued" />
                  </span>
                  <Text as="span" tone="subdued">
                    Tổng số dữ liệu
                  </Text>
                </InlineStack>
                <Text variant="headingLg" as="p">
                  {helpers.formatNumberWithCommas(sum?.count || 0)}
                </Text>
              </BlockStack>
            </Card>
          </Box>
          <Box minWidth={isMobile ? '100%' : '220px'} width={isMobile ? '100%' : undefined}>
            <Card padding="300">
              <BlockStack gap="100">
                <InlineStack gap="200" blockAlign="center" align="start" wrap={false}>
                  <span>
                    <Icon source={CashDollarIcon} tone="subdued" />
                  </span>
                  <Text as="span" tone="subdued">
                    Tổng tiền
                  </Text>
                </InlineStack>
                <Text variant="headingLg" as="p">
                  {helpers.formatNumberWithCommas(sum?.TgTTTBSo || 0)}
                </Text>
              </BlockStack>
            </Card>
          </Box>
          <Box minWidth={isMobile ? '100%' : '220px'} width={isMobile ? '100%' : undefined}>
            <Card padding="300">
              <BlockStack gap="100">
                <InlineStack gap="200" blockAlign="center" align="start" wrap={false}>
                  <span>
                    <Icon source={TaxFilledIcon} tone="subdued" />
                  </span>
                  <Text as="span" tone="subdued">
                    Tổng thuế
                  </Text>
                </InlineStack>
                <Text variant="headingLg" as="p">
                  {helpers.formatNumberWithCommas(sum?.TgTThue || 0)}
                </Text>
              </BlockStack>
            </Card>
          </Box>
          <Box minWidth={isMobile ? '100%' : '220px'} width={isMobile ? '100%' : undefined}>
            <Card padding="300">
              <BlockStack gap="100">
                <InlineStack gap="200" blockAlign="center" align="start" wrap={false}>
                  <span>
                    <Icon source={BillFilledIcon} tone="subdued" />
                  </span>
                  <Text as="span" tone="subdued">
                    Tổng thành tiền
                  </Text>
                </InlineStack>
                <Text variant="headingLg" as="p">
                  {helpers.formatNumberWithCommas(sum?.TgThTien || 0)}
                </Text>
              </BlockStack>
            </Card>
          </Box>
        </InlineStack>
      </BlockStack>
      <Box paddingBlockEnd="400">
        <Card padding="0">
          <div className="invoice_managers_wrapper">
            <SimpleFilter
              loading={loading}
              onCallback={filterCallback}
              sortField={[
                { label: 'Tổng tiền', field: 'TgTTTBSo' },
                { label: 'Ngày ký', field: 'signedAt' },
                // { label: 'Ngày khởi hành', field: 'trip_manifest.departure_date' },
              ]}
              options={[
                {
                  field: 'mccqt',
                  value: '!null',
                  label: 'Đã cấp mã',
                },
                {
                  field: 'mccqt',
                  value: 'null',
                  label: 'Chưa cấp mã',
                },
              ]}
              defaultSort="signedAt:desc"
            />
            <IndexTable
              resourceName={resourceName}
              itemCount={entities.length}
              hasZebraStriping
              selectable={false}
              emptyState={
                <Text as="p" tone="subdued">
                  {loading ? 'Đang tải dữ liệu...' : 'Không có hóa đơn đã ký.'}
                </Text>
              }
              headings={[
                { title: 'Số HĐ' },
                { title: 'Người ký' },
                { title: 'Ngày ký' },
                { title: 'T.Trạng CQT' },
                { title: 'Biển số' },
                { title: 'Chuyến' },
                { title: 'Khởi hành' },
                { title: 'Loại thuế' },
                { title: 'Tên người mua' },
                { title: 'CCCD' },
                { title: 'Tên đơn vị' },
                { title: 'MST' },
                { title: 'SĐT' },
                { title: 'Email' },
                { title: 'Địa chỉ' },
                { title: 'Thuế', alignment: 'end' },
                { title: 'Thành tiền', alignment: 'end' },
                { title: 'Tổng tiền', alignment: 'end' },
                { title: 'Ngày tạo' },
                { title: 'Mã tra cứu' },
                { title: 'Mã tham chiếu' },
              ]}
            >
              {rowMarkup}
            </IndexTable>
          </div>
        </Card>
      </Box>

      {totalItems > 0 ? (
        <Pagination
          TotalRecord={totalItems}
          onChange={onChangePagination}
          pageSize={Number(mainQuery.limit)}
          activeCurrentPage={Number(mainQuery.page)}
        />
      ) : null}
    </BlockStack>
  );
}

